import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type UpdateData,
} from 'firebase/firestore';
import { db } from './firebase';
import type { EnhancedDeal } from '../types';
import {
  defaultClientLabelFromLead,
  getProjectDisplayName,
  isLegacyDealName,
} from './projectName';

/** Firestore update payloads (FieldValue + scalars). */
type DbPatch = UpdateData<DocumentData>;

function composeLeadDisplayName(firstName?: string, lastName?: string): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

export type ProposalClientIdentityPatch = {
  clientName?: string;
  leadCompany?: string;
};

/**
 * Keep public proposal "Prepared for" fields in sync when staff edits the lead
 * or portal client profile. Clears cached signed PDFs so downloads regenerate
 * with the updated name/company.
 */
export async function syncProposalClientIdentityForDeal(
  dealId: string,
  patch: ProposalClientIdentityPatch,
): Promise<number> {
  const trimmedName = patch.clientName?.trim();
  const trimmedCompany = patch.leadCompany?.trim();
  if (!dealId || (!trimmedName && trimmedCompany === undefined)) return 0;

  const snap = await getDocs(
    query(collection(db, 'proposalBuilders'), where('dealId', '==', dealId)),
  );
  if (snap.empty) return 0;

  const batch = writeBatch(db);
  snap.docs.forEach(d => {
    const update = {
      updatedAt: Timestamp.now(),
      signedPdfUrl: deleteField(),
      ...(trimmedName ? { clientName: trimmedName } : {}),
      ...(trimmedCompany !== undefined ? { leadCompany: trimmedCompany } : {}),
    } as DbPatch;
    batch.update(d.ref, update);
  });
  await batch.commit();
  return snap.size;
}

export async function syncProposalClientIdentityForDeals(
  dealIds: Iterable<string>,
  patch: ProposalClientIdentityPatch,
): Promise<number> {
  const unique = Array.from(new Set(Array.from(dealIds).filter(Boolean)));
  let updated = 0;
  for (const dealId of unique) {
    updated += await syncProposalClientIdentityForDeal(dealId, patch);
  }
  return updated;
}

/**
 * After a lead name/company edit (any CRM surface), fan out to portals,
 * enhanced deals, and proposal builders so the public proposal page can
 * update via Firestore listeners without a manual refresh.
 */
function buildEnhancedDealIdentityPatch(
  deal: EnhancedDeal,
  identity: { firstName?: string; lastName?: string; company?: string },
): DbPatch {
  const clientName = composeLeadDisplayName(identity.firstName, identity.lastName);
  const leadCompany = identity.company ?? '';
  const leadLike = {
    firstName: identity.firstName || '',
    lastName: identity.lastName || '',
    company: identity.company,
  };
  const nameChanged = identity.firstName !== undefined || identity.lastName !== undefined;
  const companyChanged = identity.company !== undefined;

  const patch = {
    updatedAt: Timestamp.now(),
    ...(clientName ? { contactName: clientName } : {}),
    ...(companyChanged
      ? { company: leadCompany.length > 0 ? leadCompany : deleteField() }
      : {}),
  } as DbPatch;

  const shouldRefreshCanonicalName = nameChanged || companyChanged;
  if (!shouldRefreshCanonicalName) return patch;

  if (isLegacyDealName(deal)) {
    if (clientName) {
      const projName = deal.title?.includes(' - ')
        ? deal.title.split(' - ').slice(1).join(' - ')
        : (deal.descriptor || '').trim() || deal.title || '';
      (patch as DocumentData).title = projName ? `${clientName} - ${projName}` : clientName;
    }
    return patch;
  }

  const nextLabel = defaultClientLabelFromLead(leadLike);
  if (nextLabel) {
    (patch as DocumentData).clientLabel = nextLabel;
    (patch as DocumentData).title = getProjectDisplayName({
      projectNumber: deal.projectNumber,
      clientLabel: nextLabel,
      descriptor: deal.descriptor,
      title: deal.title,
    });
  }
  return patch;
}

export async function propagateLeadClientIdentityToProjections(
  leadId: string,
  identity: { firstName?: string; lastName?: string; company?: string },
): Promise<void> {
  if (!leadId) return;
  const clientName = composeLeadDisplayName(identity.firstName, identity.lastName);
  const leadCompany = identity.company ?? '';

  const dealSnap = await getDocs(
    query(collection(db, 'enhancedDeals'), where('leadId', '==', leadId)),
  );
  const dealIds = dealSnap.docs.map(d => d.id);

  await Promise.all(
    dealSnap.docs.map(async d => {
      const deal = { id: d.id, ...(d.data() as EnhancedDeal) };
      const patch = buildEnhancedDealIdentityPatch(deal, identity);
      try {
        await updateDoc(doc(db, 'enhancedDeals', d.id), patch);
      } catch (err) {
        console.warn('[propagateLeadClientIdentity] deal update failed', d.id, err);
      }
    }),
  );

  try {
    const portalSnap = await getDocs(
      query(collection(db, 'clientPortals'), where('leadId', '==', leadId)),
    );
    await Promise.all(
      portalSnap.docs.map(async p => {
        const patch = {
          updatedAt: Timestamp.now(),
          ...(clientName ? { clientName } : {}),
          ...(identity.company !== undefined ? { clientCompany: leadCompany } : {}),
        } as DbPatch;
        try {
          await updateDoc(doc(db, 'clientPortals', p.id), patch);
        } catch (err) {
          console.warn('[propagateLeadClientIdentity] portal update failed', p.id, err);
        }
      }),
    );
  } catch (err) {
    console.warn('[propagateLeadClientIdentity] portal query failed', err);
  }

  if (clientName || identity.company !== undefined) {
    await syncProposalClientIdentityForDeals(dealIds, {
      clientName: clientName || undefined,
      leadCompany: identity.company !== undefined ? leadCompany : undefined,
    });
  }
}
